import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/auth/useAuth'
import * as api from './api'

const tenantKey = (tenantId: string, ...parts: unknown[]) => ['tenant', tenantId, 'payroll', ...parts]

export function useDashboardTiles() {
  const { user } = useAuth()
  const tenantId = user?.tenantId
  return useQuery({
    queryKey: tenantKey(tenantId ?? 'anon', 'dashboard'),
    queryFn: () => api.getDashboardTiles(),
    enabled: Boolean(tenantId),
    staleTime: 30_000,
  })
}

export function useEmployees(opts: { q?: string; status?: string } = {}) {
  const { user } = useAuth()
  const tenantId = user?.tenantId
  return useQuery({
    queryKey: tenantKey(tenantId ?? 'anon', 'employees', opts.q ?? '', opts.status ?? ''),
    queryFn: () => api.listEmployees(opts),
    enabled: Boolean(tenantId),
    staleTime: 0,
    refetchOnMount: 'always',
  })
}

export function useEmployee(id: string | undefined) {
  const { user } = useAuth()
  const tenantId = user?.tenantId
  return useQuery({
    queryKey: tenantKey(tenantId ?? 'anon', 'employee', id),
    queryFn: () => api.getEmployee(id as string),
    enabled: Boolean(tenantId) && Boolean(id),
  })
}

export function useCreateEmployee() {
  const qc = useQueryClient()
  const { user } = useAuth()
  const tenantId = user?.tenantId
  return useMutation({
    mutationFn: api.createEmployee,
    onSuccess: () => {
      if (tenantId) qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'employees') })
    },
  })
}

export function useUpdateEmployee() {
  const qc = useQueryClient()
  const { user } = useAuth()
  const tenantId = user?.tenantId
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Parameters<typeof api.updateEmployee>[1] }) =>
      api.updateEmployee(id, body),
    onSuccess: (_data, vars) => {
      if (tenantId) {
        qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'employees') })
        qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'employee', vars.id) })
      }
    },
  })
}

export function useEmployeeW4(id: string | undefined) {
  const { user } = useAuth()
  const tenantId = user?.tenantId
  return useQuery({
    queryKey: tenantKey(tenantId ?? 'anon', 'employee-w4', id),
    queryFn: () => api.getEmployeeW4(id as string),
    enabled: Boolean(tenantId) && Boolean(id),
  })
}

export function useUpdateEmployeeW4() {
  const qc = useQueryClient()
  const { user } = useAuth()
  const tenantId = user?.tenantId
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: api.EmployeeW4Input }) =>
      api.updateEmployeeW4(id, body),
    onSuccess: (_data, vars) => {
      if (tenantId) qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'employee-w4', vars.id) })
    },
  })
}

export function useEmployeeI9(id: string | undefined) {
  const { user } = useAuth()
  const tenantId = user?.tenantId
  return useQuery({
    queryKey: tenantKey(tenantId ?? 'anon', 'employee-i9', id),
    queryFn: () => api.getEmployeeI9(id as string),
    enabled: Boolean(tenantId) && Boolean(id),
  })
}

export function useUpdateEmployeeI9() {
  const qc = useQueryClient()
  const { user } = useAuth()
  const tenantId = user?.tenantId
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: api.EmployeeI9Input }) =>
      api.updateEmployeeI9(id, body),
    onSuccess: (_data, vars) => {
      if (tenantId) qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'employee-i9', vars.id) })
    },
  })
}

export function useContractors(opts: { q?: string; status?: string } = {}) {
  const { user } = useAuth()
  const tenantId = user?.tenantId
  return useQuery({
    queryKey: tenantKey(tenantId ?? 'anon', 'contractors', opts.q ?? '', opts.status ?? ''),
    queryFn: () => api.listContractors(opts),
    enabled: Boolean(tenantId),
    staleTime: 0,
    refetchOnMount: 'always',
  })
}

export function useCreateContractor() {
  const qc = useQueryClient()
  const { user } = useAuth()
  const tenantId = user?.tenantId
  return useMutation({
    mutationFn: api.createContractor,
    onSuccess: () => {
      if (tenantId) qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'contractors') })
    },
  })
}

export function usePayComponents(type?: string) {
  const { user } = useAuth()
  const tenantId = user?.tenantId
  return useQuery({
    queryKey: tenantKey(tenantId ?? 'anon', 'components', type ?? ''),
    queryFn: () => api.listComponents({ type }),
    enabled: Boolean(tenantId),
  })
}

export function useCreatePayComponent() {
  const qc = useQueryClient()
  const { user } = useAuth()
  const tenantId = user?.tenantId
  return useMutation({
    mutationFn: api.createComponent,
    onSuccess: () => {
      if (tenantId) qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'components') })
    },
  })
}

export function useSetupUsComponents() {
  const qc = useQueryClient()
  const { user } = useAuth()
  const tenantId = user?.tenantId
  return useMutation({
    mutationFn: api.setupUsComponents,
    onSuccess: () => {
      if (tenantId) qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'components') })
    },
  })
}

export function usePaySchedules() {
  const { user } = useAuth()
  const tenantId = user?.tenantId
  return useQuery({
    queryKey: tenantKey(tenantId ?? 'anon', 'pay-schedules'),
    queryFn: () => api.listPaySchedules(),
    enabled: Boolean(tenantId),
  })
}

export function useCreatePaySchedule() {
  const qc = useQueryClient()
  const { user } = useAuth()
  const tenantId = user?.tenantId
  return useMutation({
    mutationFn: api.createPaySchedule,
    onSuccess: () => {
      if (tenantId) qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'pay-schedules') })
    },
  })
}

export function usePayRuns(opts: { status?: string; limit?: number } = {}) {
  const { user } = useAuth()
  const tenantId = user?.tenantId
  return useQuery({
    queryKey: tenantKey(tenantId ?? 'anon', 'pay-runs', opts.status ?? '', opts.limit ?? 0),
    queryFn: () => api.listPayRuns(opts),
    enabled: Boolean(tenantId),
    staleTime: 0,
    refetchOnMount: 'always',
  })
}

export function usePayRun(id: string | undefined) {
  const { user } = useAuth()
  const tenantId = user?.tenantId
  return useQuery({
    queryKey: tenantKey(tenantId ?? 'anon', 'pay-run', id),
    queryFn: () => api.getPayRun(id as string),
    enabled: Boolean(tenantId) && Boolean(id),
  })
}

export function useCreatePayRun() {
  const qc = useQueryClient()
  const { user } = useAuth()
  const tenantId = user?.tenantId
  return useMutation({
    mutationFn: api.createPayRun,
    onSuccess: () => {
      if (tenantId) qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'pay-runs') })
    },
  })
}
